/**
 * SuperAdminDashboard - Cross-Market Overview for Super Admins
 *
 * Sections:
 *   1. Market Health Cards — per-market venue/deal/event counts, stale %
 *   2. Aggregate Totals — cross-market sums
 *   3. User Overview — accounts by role/tier, recent signups
 *   4. Inbox — pending submissions from public forms across all markets
 *   5. Recent Activity Feed — last 20 audit entries
 *   6. Scraper Status — per-source health indicators
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useMarketNavigate } from '../hooks/useMarketNavigate';
import { MarketLink } from '../components/MarketLink';
import SEOHead from '../components/SEOHead';
import { StatCard, AnalyticsLineChart, LeaderboardTable, FunnelChart } from '../components/analytics';
import './SuperAdminDashboard.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

function SuperAdminDashboard() {
  const { user, getToken, logout: authLogout } = useAuth();
  const navigate = useMarketNavigate();

  // Data state
  const [health, setHealth] = useState(null);
  const [users, setUsers] = useState(null);
  const [activity, setActivity] = useState(null);
  const [scrapers, setScrapers] = useState(null);
  const [inbox, setInbox] = useState(null);

  // Loading state
  const [healthLoading, setHealthLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(true);
  const [scrapersLoading, setScrapersLoading] = useState(true);
  const [inboxLoading, setInboxLoading] = useState(true);

  // Collapsible section state
  const [usersOpen, setUsersOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [scrapersOpen, setScrapersOpen] = useState(false);
  const [testRunnerOpen, setTestRunnerOpen] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);

  // Inbox review state
  const [reviewingIds, setReviewingIds] = useState(new Set());
  const [seenInboxCount, setSeenInboxCount] = useState(0);

  // Venue claim approval state
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [claimModalData, setClaimModalData] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [restaurantsLoading, setRestaurantsLoading] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState('');
  const [approving, setApproving] = useState(false);
  const [claimError, setClaimError] = useState('');
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successModalData, setSuccessModalData] = useState(null);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [userStatuses, setUserStatuses] = useState({});

  // API Test Runner state
  const [testSuite, setTestSuite] = useState('all');
  const [isRunningTests, setIsRunningTests] = useState(false);
  const [testConsoleOutput, setTestConsoleOutput] = useState([]);
  const [isTestConsoleExpanded, setIsTestConsoleExpanded] = useState(true);
  const testConsoleRef = useRef(null);

  // GA4 Analytics state
  const [analyticsOpen, setAnalyticsOpen] = useState(true);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('7d');
  const [traffic, setTraffic] = useState(null);
  const [topVenues, setTopVenues] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [realtimeUsers, setRealtimeUsers] = useState(null);
  const [trafficLoading, setTrafficLoading] = useState(true);
  const [topVenuesLoading, setTopVenuesLoading] = useState(true);
  const [funnelLoading, setFunnelLoading] = useState(true);

  // Errors
  const [errors, setErrors] = useState({});

  const getAuthHeaders = useCallback(() => {
    const token = getToken();
    return {
      'Authorization': token ? `Bearer ${token}` : '',
    };
  }, [getToken]);

  const fetchData = useCallback(async (endpoint, setter, loadingSetter, errorKey) => {
    loadingSetter(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/dashboard/${endpoint}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setter(data);
      setErrors(prev => ({ ...prev, [errorKey]: null }));
    } catch (err) {
      setErrors(prev => ({ ...prev, [errorKey]: err.message }));
    } finally {
      loadingSetter(false);
    }
  }, [getAuthHeaders]);

  // Fetch analytics data from GA4 endpoints
  const fetchAnalytics = useCallback(async (period) => {
    const headers = getAuthHeaders();
    const base = `${API_BASE}/api/analytics`;

    // Traffic overview
    setTrafficLoading(true);
    fetch(`${base}/traffic?scope=all&period=${period}`, { headers })
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Failed')))
      .then(d => { setTraffic(d); setErrors(prev => ({ ...prev, traffic: null })); })
      .catch(err => setErrors(prev => ({ ...prev, traffic: err.message })))
      .finally(() => setTrafficLoading(false));

    // Top venues
    setTopVenuesLoading(true);
    fetch(`${base}/top-venues?scope=all&limit=10&period=${period}`, { headers })
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Failed')))
      .then(d => { setTopVenues(d); setErrors(prev => ({ ...prev, topVenues: null })); })
      .catch(err => setErrors(prev => ({ ...prev, topVenues: err.message })))
      .finally(() => setTopVenuesLoading(false));

    // Conversion funnel
    setFunnelLoading(true);
    fetch(`${base}/funnel?scope=all&period=${period}`, { headers })
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Failed')))
      .then(d => { setFunnel(d); setErrors(prev => ({ ...prev, funnel: null })); })
      .catch(err => setErrors(prev => ({ ...prev, funnel: err.message })))
      .finally(() => setFunnelLoading(false));

    // Realtime (fire and forget — no loading state)
    fetch(`${base}/realtime?scope=all`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setRealtimeUsers(d))
      .catch(() => {});
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData('health', setHealth, setHealthLoading, 'health');
    fetchData('users', setUsers, setUsersLoading, 'users');
    fetchData('activity', d => setActivity(d.activity), setActivityLoading, 'activity');
    fetchData('scrapers', setScrapers, setScrapersLoading, 'scrapers');
    fetchData('inbox', setInbox, setInboxLoading, 'inbox');
    fetchAnalytics(analyticsPeriod);
  }, [fetchData, fetchAnalytics, analyticsPeriod]);

  // Poll realtime users every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      const headers = getAuthHeaders();
      fetch(`${API_BASE}/api/analytics/realtime?scope=all`, { headers })
        .then(r => r.ok ? r.json() : null)
        .then(d => d && setRealtimeUsers(d))
        .catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, [getAuthHeaders]);

  const handleLogout = async () => {
    await authLogout();
    navigate('/login');
  };

  // ── API Test Runner ──
  const addTestConsoleLog = useCallback((message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    let cleanMessage = message;
    const prefixes = ['INFO:__main__:', 'WARNING:__main__:', 'ERROR:__main__:', 'DEBUG:__main__:'];
    for (const prefix of prefixes) {
      if (cleanMessage.startsWith(prefix)) {
        cleanMessage = cleanMessage.substring(prefix.length);
        break;
      }
    }
    if (!cleanMessage.trim() || cleanMessage.match(/^[=]+$/)) return;
    setTestConsoleOutput(prev => [...prev, { timestamp, message: cleanMessage, type }]);
  }, []);

  const handleRunTests = async () => {
    setIsRunningTests(true);
    setTestConsoleOutput([]);
    setIsTestConsoleExpanded(true);

    try {
      const response = await fetch(`${API_BASE}/api/run-tests`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ suite: testSuite })
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      if (!response.body) throw new Error('Response body is null');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));
              if (data.type === 'complete') {
                addTestConsoleLog(data.message, data.hasErrors ? 'error' : 'success');
              } else {
                addTestConsoleLog(data.message, data.type);
              }
            } catch (e) { /* skip parse errors */ }
          }
        }
      }
    } catch (error) {
      addTestConsoleLog(`Error: ${error.message}`, 'error');
    } finally {
      setIsRunningTests(false);
    }
  };

  const copyTestConsole = () => {
    const text = testConsoleOutput.map(l => `[${l.timestamp}] ${l.message}`).join('\n');
    navigator.clipboard.writeText(text);
  };

  const downloadTestConsole = () => {
    const text = testConsoleOutput.map(l => `[${l.timestamp}] ${l.message}`).join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `test-output-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Auto-scroll test console
  useEffect(() => {
    if (testConsoleRef.current) {
      testConsoleRef.current.scrollTop = testConsoleRef.current.scrollHeight;
    }
  }, [testConsoleOutput]);

  // Helper: format number with commas
  const fmt = (n) => (n || 0).toLocaleString();

  // Helper: compute aggregate totals from market health
  const getAggregates = () => {
    if (!health?.markets) return null;
    const agg = { venues: 0, deals: 0, specials: 0, events: 0 };
    Object.values(health.markets).forEach(m => {
      if (m.metrics?.success) {
        agg.venues += m.metrics.activeRestaurants || 0;
        agg.deals += m.metrics.activeHappyHours || 0;
        agg.specials += m.metrics.activeSpecials || 0;
        agg.events += m.metrics.activeEvents || 0;
      }
    });
    return agg;
  };

  // Helper: bar width for comparison charts
  const barWidth = (value, max) => max > 0 ? Math.max(8, (value / max) * 100) : 0;

  // Market colors for card headers and comparison bars
  const MARKET_COLORS = ['#667eea', '#f59e0b', '#10b981', '#ec4899', '#8b5cf6', '#06b6d4'];
  const getMarketColor = (index) => MARKET_COLORS[index % MARKET_COLORS.length];

  // Market display names (location context for disambiguation)
  const MARKET_DISPLAY_NAMES = {
    otc_naperville: 'OTC Naperville',
    'otc-naperville': 'OTC Naperville',
    otc_fox_valley_south: 'OTC Fox Valley South',
    'otc-fox-valley-south': 'OTC Fox Valley South',
    tcc: 'TCC Tri-Cities',
  };

  /** Build a human-readable summary line for an audit log entry */
  const formatActivitySummary = (log) => {
    const d = log.details;
    switch (log.action) {
      case 'login':
        return null; // email is enough context
      case 'logout':
        return null;
      case 'setting_changed': {
        if (!d) return null;
        const parts = [];
        if (d.tierChanges) {
          if (d.tierChanges.Tier) parts.push(`Tier → ${d.tierChanges.Tier}`);
          if (d.tierChanges.is_featured !== undefined) parts.push(d.tierChanges.is_featured ? 'Featured' : 'Unfeatured');
        }
        if (d.fields && parts.length === 0) {
          parts.push(`Changed: ${d.fields.join(', ')}`);
        }
        return parts.length > 0 ? parts.join(', ') : null;
      }
      case 'content_approved': {
        if (!d) return null;
        const name = d.restaurantName || '';
        const type = d.contentType || 'content';
        const count = d.publishedCount || 0;
        return name
          ? `${count} ${type}(s) for ${name}`
          : `${count} ${type}(s) published`;
      }
      case 'content_rejected': {
        if (!d) return null;
        const name = d.restaurantName || '';
        const type = d.contentType || 'content';
        return name ? `${type} from ${name}` : type;
      }
      case 'user_created': {
        if (!d) return null;
        return `${d.email || ''} (${d.role || 'user'})`;
      }
      case 'user_updated': {
        if (!d) return null;
        if (d.status === 'disabled') return 'Account disabled';
        if (d.fields) return `Updated: ${Object.keys(d.fields).join(', ')}`;
        return null;
      }
      case 'password_changed': {
        if (!d) return null;
        return d.method === 'self-service' ? 'Self-service password change' : null;
      }
      case 'scrape_triggered':
        return d?.scope ? `Scope: ${d.scope}` : null;
      default:
        return null;
    }
  };

  // ── Inbox helpers ──
  const getInboxTotal = () => {
    if (!inbox?.markets) return 0;
    return Object.values(inbox.markets).reduce(
      (sum, m) => sum + (m.submissions?.length || 0), 0
    );
  };

  const handleMarkReviewed = async (marketKey, recordId) => {
    setReviewingIds(prev => new Set([...prev, recordId]));
    try {
      const res = await fetch(`${API_BASE}/api/admin/dashboard/inbox/${marketKey}/${recordId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to mark as reviewed');
      // Optimistic update: remove the item from the local state
      setInbox(prev => {
        if (!prev?.markets?.[marketKey]) return prev;
        return {
          ...prev,
          markets: {
            ...prev.markets,
            [marketKey]: {
              ...prev.markets[marketKey],
              submissions: prev.markets[marketKey].submissions.filter(s => s.id !== recordId),
            },
          },
        };
      });
    } catch (err) {
      console.error('Mark reviewed error:', err);
    } finally {
      setReviewingIds(prev => {
        const next = new Set(prev);
        next.delete(recordId);
        return next;
      });
    }
  };

  // Upgrade approval state
  const [upgradingIds, setUpgradingIds] = useState(new Set());
  const [upgradeSuccessModal, setUpgradeSuccessModal] = useState(null);

  const handleApproveUpgrade = async (sub) => {
    setUpgradingIds(prev => new Set([...prev, sub.id]));
    try {
      const res = await fetch(`${API_BASE}/api/admin/dashboard/approve-upgrade`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          marketKey: sub.marketKey,
          submissionId: sub.id,
          user_id: sub.user_id,
          venue_id: sub.venue_id,
          requested_tier: sub.requested_tier || 'silver',
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }

      // Show success modal
      setUpgradeSuccessModal({
        name: sub.name || sub.email,
        venue_name: sub.venue_name,
        requested_tier: sub.requested_tier || 'silver',
      });

      // Remove from inbox (optimistic)
      setInbox(prev => {
        if (!prev?.markets?.[sub.marketKey]) return prev;
        return {
          ...prev,
          markets: {
            ...prev.markets,
            [sub.marketKey]: {
              ...prev.markets[sub.marketKey],
              submissions: prev.markets[sub.marketKey].submissions.filter(s => s.id !== sub.id),
            },
          },
        };
      });
    } catch (err) {
      console.error('Approve upgrade error:', err);
      alert(`Failed to approve upgrade: ${err.message}`);
    } finally {
      setUpgradingIds(prev => {
        const next = new Set(prev);
        next.delete(sub.id);
        return next;
      });
    }
  };

  const INBOX_TYPE_CONFIG = {
    venue_submission:  { label: 'Venue',   color: '#16a34a', bg: '#dcfce7' },
    venue_claim:       { label: 'Claim',   color: '#7c3aed', bg: '#ede9fe' },
    ad_inquiry:        { label: 'Ad',      color: '#2563eb', bg: '#dbeafe' },
    contact:           { label: 'Contact', color: '#d97706', bg: '#fef3c7' },
    upgrade_request:   { label: 'Upgrade', color: '#9333ea', bg: '#f3e8ff' },
  };

  // ── Venue Claim Approval Handlers ──

  const handleOpenClaimModal = async (sub) => {
    setClaimModalData(sub);
    setClaimModalOpen(true);
    setSelectedRestaurant('');
    setClaimError('');
    setRestaurantsLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/admin/dashboard/restaurants/${sub.marketKey}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to fetch restaurants');
      const data = await res.json();
      setRestaurants(data.restaurants || []);
    } catch (err) {
      setClaimError('Failed to load restaurant list');
      setRestaurants([]);
    } finally {
      setRestaurantsLoading(false);
    }
  };

  const handleApproveClaim = async () => {
    if (!selectedRestaurant || !claimModalData) return;
    setApproving(true);
    setClaimError('');

    try {
      const res = await fetch(`${API_BASE}/api/admin/dashboard/approve-claim`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          marketKey: claimModalData.marketKey,
          submissionId: claimModalData.id,
          restaurantId: selectedRestaurant,
          email: claimModalData.email,
          name: claimModalData.name,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }

      const data = await res.json();

      // Close claim modal, open success modal
      setClaimModalOpen(false);
      setSuccessModalData({
        email: claimModalData.email,
        name: claimModalData.name,
        venueName: data.venueName,
        loginSlug: data.loginSlug,
        marketKey: claimModalData.marketKey,
      });
      setSuccessModalOpen(true);
      setCopiedEmail(false);

      // Remove from inbox list (optimistic)
      setInbox(prev => {
        if (!prev?.markets?.[claimModalData.marketKey]) return prev;
        return {
          ...prev,
          markets: {
            ...prev.markets,
            [claimModalData.marketKey]: {
              ...prev.markets[claimModalData.marketKey],
              submissions: prev.markets[claimModalData.marketKey].submissions.filter(s => s.id !== claimModalData.id),
            },
          },
        };
      });
    } catch (err) {
      setClaimError(err.message);
    } finally {
      setApproving(false);
    }
  };

  const MARKET_LOGIN_URLS = {
    otc_naperville: 'https://www.offtheclockspots.com/naperville/login',
    otc_fox_valley_south: 'https://www.offtheclockspots.com/aurora/login',
    tcc: 'https://tcc-happyhour-frontend.onrender.com/login',
  };

  const MARKET_DASHBOARD_URLS = {
    otc_naperville: 'https://www.offtheclockspots.com/naperville/admin/venue',
    otc_fox_valley_south: 'https://www.offtheclockspots.com/aurora/admin/venue',
    tcc: 'https://tcc-happyhour-frontend.onrender.com/admin/venue',
  };

  const generateWelcomeEmail = (data) => {
    const greeting = data.name
      ? `Hi ${data.name},`
      : `Hello team ${data.venueName},`;

    const loginUrl = MARKET_LOGIN_URLS[data.marketKey] || 'https://www.offtheclockspots.com/login';
    const dashboardUrl = MARKET_DASHBOARD_URLS[data.marketKey] || 'https://www.offtheclockspots.com/admin/venue';

    return `${greeting}

Thanks for claiming your listing on Off the Clock Spots! Your venue owner account is all set up and ready to go.

Here's how to get started:

1. Go to your login page: ${loginUrl}
2. Enter your email: ${data.email}
3. Enter your temporary password: Welcome2026!
4. You'll be asked to set your own password right away.
5. Once you're in, you'll land on your Venue Owner Dashboard: ${dashboardUrl}

From your dashboard, you can update your venue info, add happy hour deals, post events, and more.

If you need help at any point, check out our Venue Owner Guide:
https://www.offtheclockspots.com/guide

Or just reply to this email and I'll be happy to help.

Cheers,
Kevin Howe
Off the Clock Spots
kevin@offtheclockspots.com`;
  };

  const handleCopyEmail = () => {
    if (!successModalData) return;
    const emailText = generateWelcomeEmail(successModalData);
    navigator.clipboard.writeText(emailText);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 3000);
  };

  // Fetch user status (red/green dot) for venue_claim submissions
  const fetchUserStatus = useCallback(async (email) => {
    if (userStatuses[email] !== undefined) return; // already fetched or loading
    setUserStatuses(prev => ({ ...prev, [email]: 'loading' }));
    try {
      const res = await fetch(`${API_BASE}/api/admin/dashboard/user-status/${encodeURIComponent(email)}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setUserStatuses(prev => ({ ...prev, [email]: data }));
    } catch {
      setUserStatuses(prev => ({ ...prev, [email]: null }));
    }
  }, [getAuthHeaders, userStatuses]);

  // Fetch user statuses for all venue_claim submissions when inbox loads
  useEffect(() => {
    if (!inbox?.markets) return;
    Object.values(inbox.markets).forEach(market => {
      (market.submissions || []).forEach(sub => {
        if (sub.type === 'venue_claim' && sub.email) {
          fetchUserStatus(sub.email);
        }
      });
    });
  }, [inbox]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="sad-page">
      <SEOHead title="Dashboard" noindex />
      {/* Header */}
      <header className="sad-header">
        <div className="sad-header-content">
          <div className="sad-header-left">
            <h1>Super Admin Dashboard</h1>
            <p className="sad-header-user">{user?.name || user?.email}</p>
          </div>
          <div className="sad-header-actions">
            <MarketLink to="/admin" className="sad-nav-link">Admin Console</MarketLink>
            <MarketLink to="/" className="sad-nav-link">Back to Site</MarketLink>
            <button onClick={handleLogout} className="sad-logout-btn">Logout</button>
          </div>
        </div>
      </header>

      <div className="sad-container">

        {/* ── Aggregate Totals ── */}
        {(() => {
          const agg = getAggregates();
          if (!agg && healthLoading) return <div className="sad-loading">Loading totals...</div>;
          if (!agg) return null;
          return (
            <div className="sad-totals">
              <div className="sad-total-card sad-total-venues">
                <span className="sad-total-icon">🍽️</span>
                <div className="sad-total-content">
                  <span className="sad-total-value">{fmt(agg.venues)}</span>
                  <span className="sad-total-label">Total Venues</span>
                </div>
              </div>
              <div className="sad-total-card sad-total-deals">
                <span className="sad-total-icon">🍺</span>
                <div className="sad-total-content">
                  <span className="sad-total-value">{fmt(agg.deals)}</span>
                  <span className="sad-total-label">Happy Hours</span>
                </div>
              </div>
              <div className="sad-total-card sad-total-specials">
                <span className="sad-total-icon">⭐</span>
                <div className="sad-total-content">
                  <span className="sad-total-value">{fmt(agg.specials)}</span>
                  <span className="sad-total-label">Specials</span>
                </div>
              </div>
              <div className="sad-total-card sad-total-events">
                <span className="sad-total-icon">🎉</span>
                <div className="sad-total-content">
                  <span className="sad-total-value">{fmt(agg.events)}</span>
                  <span className="sad-total-label">Events</span>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── Market Health Cards ── */}
        <section className="sad-section">
          <h2>Market Health</h2>
          {healthLoading && <div className="sad-loading">Loading market health...</div>}
          {errors.health && <div className="sad-error">{errors.health}</div>}
          {health?.markets && (
            <div className="sad-market-grid">
              {Object.entries(health.markets).map(([key, market], index) => {
                const m = market.metrics;
                const color = getMarketColor(index);
                if (!m?.success) {
                  return (
                    <div key={key} className="sad-market-card sad-market-card-error">
                      <h3>{MARKET_DISPLAY_NAMES[key] || market.shortName}</h3>
                      <p className="sad-error-text">Failed to load metrics</p>
                    </div>
                  );
                }
                const stalePercent = m.activeRestaurants > 0
                  ? Math.round(((m.freshnessCounts?.stale || 0) / m.activeRestaurants) * 100)
                  : 0;
                return (
                  <div key={key} className="sad-market-card">
                    <div className="sad-market-card-header" style={{ background: `${color}20`, borderLeft: `4px solid ${color}` }}>
                      <h3>{MARKET_DISPLAY_NAMES[key] || market.shortName}</h3>
                      <span className="sad-market-name">{market.name}</span>
                    </div>
                    <div className="sad-market-stats">
                      <div className="sad-stat">
                        <span className="sad-stat-value">{fmt(m.activeRestaurants)}</span>
                        <span className="sad-stat-label">Venues</span>
                      </div>
                      <div className="sad-stat">
                        <span className="sad-stat-value">{fmt(m.activeHappyHours)}</span>
                        <span className="sad-stat-label">Happy Hours</span>
                      </div>
                      <div className="sad-stat">
                        <span className="sad-stat-value">{fmt(m.activeSpecials)}</span>
                        <span className="sad-stat-label">Specials</span>
                      </div>
                      <div className="sad-stat">
                        <span className="sad-stat-value">{fmt(m.activeEvents)}</span>
                        <span className="sad-stat-label">Events</span>
                      </div>
                    </div>
                    <div className="sad-market-footer">
                      <span className={`sad-stale-badge ${stalePercent > 30 ? 'sad-stale-warn' : 'sad-stale-ok'}`}>
                        {stalePercent}% stale
                      </span>
                      <MarketLink to={`/admin?market=${key}`} className="sad-manage-link">Manage</MarketLink>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Growth Comparison Bars */}
          {health?.markets && (() => {
            const allMarketKeys = Object.keys(health.markets);
            const marketEntries = Object.entries(health.markets).filter(([, m]) => m.metrics?.success);
            if (marketEntries.length < 2) return null;
            const maxVenues = Math.max(...marketEntries.map(([, m]) => m.metrics.activeRestaurants || 0));
            const maxDeals = Math.max(...marketEntries.map(([, m]) => m.metrics.activeHappyHours || 0));
            const maxEvents = Math.max(...marketEntries.map(([, m]) => m.metrics.activeEvents || 0));

            return (
              <div className="sad-comparison">
                <h3>Market Comparison</h3>
                {['Venues', 'Happy Hours', 'Events'].map((label, i) => (
                  <div key={label} className="sad-comparison-group">
                    <span className="sad-comparison-label">{label}</span>
                    {marketEntries.map(([key, m]) => {
                      const val = i === 0 ? m.metrics.activeRestaurants : i === 1 ? m.metrics.activeHappyHours : m.metrics.activeEvents;
                      const max = i === 0 ? maxVenues : i === 1 ? maxDeals : maxEvents;
                      const colorIndex = allMarketKeys.indexOf(key);
                      const color = getMarketColor(colorIndex);
                      return (
                        <div key={key} className="sad-bar-row">
                          <span className="sad-bar-label">{MARKET_DISPLAY_NAMES[key] || m.shortName}</span>
                          <div className="sad-bar-track">
                            <div className="sad-bar-fill" style={{ width: `${barWidth(val, max)}%`, background: color }} />
                          </div>
                          <span className="sad-bar-value">{fmt(val)}</span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            );
          })()}
        </section>

        {/* ── Traffic & Engagement Analytics (collapsible) ── */}
        <section className="sad-section sad-collapsible">
          <button className="sad-section-toggle" onClick={() => setAnalyticsOpen(!analyticsOpen)}>
            <div className="sad-toggle-title">
              <span className="sad-info-icon" data-tooltip="Site traffic and engagement analytics from Google Analytics (GA4). Data refreshes every 5 minutes.">i</span>
              <h2>Traffic & Engagement</h2>
              {realtimeUsers && (
                <span className="sad-realtime-badge">
                  <span className="sad-realtime-dot" />
                  {realtimeUsers.activeUsers} active now
                </span>
              )}
            </div>
            <span className={`sad-toggle-chevron ${analyticsOpen ? 'sad-chevron-open' : ''}`}>&#9662;</span>
          </button>
          {analyticsOpen && (
            <div className="sad-section-body">
              {/* Period selector */}
              <div className="an-period-selector">
                {['7d', '30d', '90d'].map(p => (
                  <button
                    key={p}
                    className={`an-period-btn ${analyticsPeriod === p ? 'an-period-btn--active' : ''}`}
                    onClick={() => setAnalyticsPeriod(p)}
                  >
                    {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '90 Days'}
                  </button>
                ))}
              </div>

              {errors.traffic === 'Analytics service not configured' ? (
                <div className="sad-analytics-notice">
                  <p>GA4 analytics is not configured yet. Add the <code>GA4_SERVICE_ACCOUNT_KEY</code> environment variable to enable traffic data.</p>
                </div>
              ) : (
                <>
                  {/* Stat cards row */}
                  <div className="an-stat-grid">
                    <StatCard
                      label="Page Views"
                      value={traffic ? fmt(traffic.totals.pageViews) : null}
                      icon="👁️"
                      color="#667eea"
                      loading={trafficLoading}
                    />
                    <StatCard
                      label="Active Users"
                      value={traffic ? fmt(traffic.totals.activeUsers) : null}
                      icon="👤"
                      color="#10b981"
                      loading={trafficLoading}
                    />
                    <StatCard
                      label="Sessions"
                      value={traffic ? fmt(traffic.totals.sessions) : null}
                      icon="📊"
                      color="#f59e0b"
                      loading={trafficLoading}
                    />
                    {realtimeUsers && (
                      <StatCard
                        label="Active Right Now"
                        value={realtimeUsers.activeUsers}
                        icon="🟢"
                        color="#16a34a"
                        subtitle={`as of ${new Date(realtimeUsers.timestamp).toLocaleTimeString()}`}
                      />
                    )}
                  </div>

                  {/* Daily traffic chart */}
                  <AnalyticsLineChart
                    title="Daily Traffic"
                    data={traffic?.daily || []}
                    lines={[
                      { key: 'pageViews', label: 'Page Views', color: '#667eea' },
                      { key: 'activeUsers', label: 'Active Users', color: '#10b981' },
                    ]}
                    loading={trafficLoading}
                    height={280}
                  />

                  {/* Two-column: Leaderboard + Funnel */}
                  <div className="sad-analytics-grid">
                    <LeaderboardTable
                      title="Top Venues by Engagement"
                      data={topVenues?.venues || []}
                      loading={topVenuesLoading}
                      emptyText="Venue engagement data will appear within 24-48 hours after custom dimensions are registered"
                    />
                    <FunnelChart
                      title="Conversion Funnel"
                      data={funnel?.funnel || []}
                      loading={funnelLoading}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </section>

        {/* ── User Overview (collapsible) ── */}
        <section className="sad-section sad-collapsible">
          <button className="sad-section-toggle" onClick={() => setUsersOpen(!usersOpen)}>
            <div className="sad-toggle-title">
              <span className="sad-info-icon" data-tooltip="Account totals broken down by role and tier, plus any signups from the last 7 days.">i</span>
              <h2>User Overview</h2>
            </div>
            <span className={`sad-toggle-chevron ${usersOpen ? 'sad-chevron-open' : ''}`}>&#9662;</span>
          </button>
          {usersOpen && (
            <div className="sad-section-body">
              {usersLoading && <div className="sad-loading">Loading user stats...</div>}
              {errors.users && <div className="sad-error">{errors.users}</div>}
              {users && (
                <>
                  <div className="sad-user-stats">
                    <div className="sad-user-stat-card">
                      <span className="sad-user-stat-value">{fmt(users.total)}</span>
                      <span className="sad-user-stat-label">Total Accounts</span>
                    </div>
                    {Object.entries(users.byRole || {}).map(([role, count]) => (
                      <div key={role} className="sad-user-stat-card">
                        <span className="sad-user-stat-value">{fmt(count)}</span>
                        <span className="sad-user-stat-label">{role.replace(/_/g, ' ')}</span>
                      </div>
                    ))}
                    {Object.entries(users.byTier || {}).map(([tier, count]) => (
                      <div key={tier} className="sad-user-stat-card sad-user-tier-card">
                        <span className="sad-user-stat-value">{fmt(count)}</span>
                        <span className="sad-user-stat-label">{tier} tier</span>
                      </div>
                    ))}
                  </div>
                  {users.recentSignups?.length > 0 && (
                    <div className="sad-recent-signups">
                      <h3>Recent Signups (7 days)</h3>
                      <div className="sad-signup-list">
                        {users.recentSignups.map((u, i) => (
                          <div key={i} className="sad-signup-item">
                            <strong>{u.name}</strong>
                            <span className="sad-signup-role">{u.role?.replace(/_/g, ' ')}</span>
                            <span className="sad-signup-date">
                              {u.createdAt ? new Date(u.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </section>

        {/* ── Inbox (collapsible) ── */}
        <section className="sad-section sad-collapsible">
          <button className="sad-section-toggle" onClick={() => {
            const opening = !inboxOpen;
            setInboxOpen(opening);
            if (opening) {
              setSeenInboxCount(getInboxTotal());
            }
          }}>
            <div className="sad-toggle-title">
              <span className="sad-info-icon" data-tooltip="Pending submissions from public forms — venue suggestions, advertising inquiries, and contact messages across all markets.">i</span>
              <h2>Inbox</h2>
              {!inboxOpen && getInboxTotal() > seenInboxCount && (
                <span className="sad-inbox-count">{getInboxTotal() - seenInboxCount}</span>
              )}
            </div>
            <span className={`sad-toggle-chevron ${inboxOpen ? 'sad-chevron-open' : ''}`}>&#9662;</span>
          </button>
          {inboxOpen && (
            <div className="sad-section-body">
              {inboxLoading && <div className="sad-loading">Loading inbox...</div>}
              {errors.inbox && <div className="sad-error">{errors.inbox}</div>}
              {inbox?.markets && (() => {
                const allSubmissions = [];
                Object.entries(inbox.markets).forEach(([marketKey, market]) => {
                  (market.submissions || []).forEach(sub => {
                    allSubmissions.push({ ...sub, marketKey, marketName: market.shortName || market.name });
                  });
                });
                // Sort by submitted_at descending
                allSubmissions.sort((a, b) => {
                  const da = a.submitted_at ? new Date(a.submitted_at) : new Date(0);
                  const db = b.submitted_at ? new Date(b.submitted_at) : new Date(0);
                  return db - da;
                });

                if (allSubmissions.length === 0) {
                  return <div className="sad-empty">No pending submissions.</div>;
                }

                return (
                  <div className="sad-inbox-list">
                    {allSubmissions.map(sub => {
                      const typeConf = INBOX_TYPE_CONFIG[sub.type] || { label: sub.type || '?', color: '#64748b', bg: '#f1f5f9' };
                      const isReviewing = reviewingIds.has(sub.id);
                      const isVenueClaim = sub.type === 'venue_claim';
                      const isUpgradeRequest = sub.type === 'upgrade_request';
                      const isUpgrading = upgradingIds.has(sub.id);
                      const emailStatus = sub.email ? userStatuses[sub.email] : null;
                      return (
                        <div key={sub.id} className="sad-inbox-item">
                          <span
                            className="sad-inbox-type"
                            style={{ background: typeConf.bg, color: typeConf.color }}
                          >
                            {typeConf.label}
                          </span>
                          <div className="sad-inbox-details">
                            <span className="sad-inbox-name">
                              {sub.name || sub.email || 'Anonymous'}
                              {isVenueClaim && emailStatus && emailStatus !== 'loading' && emailStatus.exists && (
                                <span
                                  className={`sad-inbox-status-dot ${emailStatus.passwordMustChange ? 'sad-status-pending' : 'sad-status-active'}`}
                                  title={emailStatus.passwordMustChange ? 'Account created, hasn\'t logged in yet' : 'Active user, password changed'}
                                />
                              )}
                            </span>
                            {sub.venue_name && (
                              <span className="sad-inbox-venue">{sub.venue_name}{sub.venue_city ? ` - ${sub.venue_city}` : ''}</span>
                            )}
                            {isUpgradeRequest && (
                              <span className="sad-inbox-tier-change">
                                {(sub.current_tier || 'Free').charAt(0).toUpperCase() + (sub.current_tier || 'free').slice(1)} → {(sub.requested_tier || 'Silver').charAt(0).toUpperCase() + (sub.requested_tier || 'silver').slice(1)}
                              </span>
                            )}
                            {sub.business_name && (
                              <span className="sad-inbox-venue">{sub.business_name}</span>
                            )}
                            {sub.message && (
                              <span className="sad-inbox-message">{sub.message}</span>
                            )}
                            {sub.email && sub.name && (
                              <span className="sad-inbox-email">{sub.email}</span>
                            )}
                          </div>
                          <span className="sad-activity-market">
                            {MARKET_DISPLAY_NAMES[sub.marketKey] || sub.marketName}
                          </span>
                          <span className="sad-inbox-time">
                            {sub.submitted_at ? new Date(sub.submitted_at).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                            }) : ''}
                          </span>
                          {isVenueClaim ? (
                            <button
                              className="sad-inbox-approve-btn"
                              onClick={() => handleOpenClaimModal(sub)}
                              title="Approve venue claim and create account"
                            >
                              Approve
                            </button>
                          ) : isUpgradeRequest ? (
                            <button
                              className="sad-inbox-upgrade-btn"
                              onClick={() => handleApproveUpgrade(sub)}
                              disabled={isUpgrading}
                              title="Approve upgrade — changes user tier and venue tier"
                            >
                              {isUpgrading ? 'Upgrading...' : '⬆ Approve Upgrade'}
                            </button>
                          ) : (
                            <button
                              className="sad-inbox-review-btn"
                              onClick={() => handleMarkReviewed(sub.marketKey, sub.id)}
                              disabled={isReviewing}
                              title="Mark as reviewed"
                            >
                              {isReviewing ? '...' : '✓ Reviewed'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}
        </section>

        {/* ── Recent Activity (collapsible) ── */}
        <section className="sad-section sad-collapsible">
          <button className="sad-section-toggle" onClick={() => setActivityOpen(!activityOpen)}>
            <div className="sad-toggle-title">
              <span className="sad-info-icon" data-tooltip="The 20 most recent actions across all users — logins, content uploads, approvals, and setting changes.">i</span>
              <h2>Recent Activity</h2>
            </div>
            <span className={`sad-toggle-chevron ${activityOpen ? 'sad-chevron-open' : ''}`}>&#9662;</span>
          </button>
          {activityOpen && (
            <div className="sad-section-body">
              {activityLoading && <div className="sad-loading">Loading activity...</div>}
              {errors.activity && <div className="sad-error">{errors.activity}</div>}
              {activity && activity.length > 0 && (
                <div className="sad-activity-list">
                  {activity.map(log => {
                    const summary = formatActivitySummary(log);
                    const marketLabel = log.market ? (MARKET_DISPLAY_NAMES[log.market] || log.market.replace(/_/g, ' ')) : null;
                    return (
                      <div key={log.id} className="sad-activity-item">
                        <span className={`sad-activity-action sad-action-${(log.action || '').split('_')[0]}`}>
                          {(log.action || '').replace(/_/g, ' ')}
                        </span>
                        <div className="sad-activity-details">
                          <span className="sad-activity-user">{log.userEmail || log.userName || log.userId}</span>
                          {summary && <span className="sad-activity-message">{summary}</span>}
                        </div>
                        {marketLabel && <span className="sad-activity-market">{marketLabel}</span>}
                        <span className="sad-activity-time">
                          {log.timestamp ? new Date(log.timestamp).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                          }) : ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              {activity && activity.length === 0 && (
                <div className="sad-empty">No recent activity.</div>
              )}
            </div>
          )}
        </section>

        {/* ── Scraper Status (collapsible) ── */}
        <section className="sad-section sad-collapsible">
          <button className="sad-section-toggle" onClick={() => setScrapersOpen(!scrapersOpen)}>
            <div className="sad-toggle-title">
              <span className="sad-info-icon" data-tooltip="Health and last-run status for each configured scraper source, organized by market.">i</span>
              <h2>Scraper Status</h2>
            </div>
            <span className={`sad-toggle-chevron ${scrapersOpen ? 'sad-chevron-open' : ''}`}>&#9662;</span>
          </button>
          {scrapersOpen && (
            <div className="sad-section-body">
              {scrapersLoading && <div className="sad-loading">Loading scraper status...</div>}
              {errors.scrapers && <div className="sad-error">{errors.scrapers}</div>}
              {scrapers?.markets && Object.entries(scrapers.markets).map(([key, market]) => {
                const sources = market.sources || [];
                const okCount = sources.filter(s => s.status === 'ok').length;
                const errorCount = sources.filter(s => s.status === 'error').length;
                const unknownCount = sources.filter(s => s.status !== 'ok' && s.status !== 'error').length;
                const staleCount = sources.filter(s => s.stale === true).length;
                return (
                  <div key={key} className="sad-scraper-market">
                    <h3>
                      {market.shortName} Sources
                      {sources.length > 0 && (
                        <span className="sad-scraper-summary">
                          <span className="sad-summary-ok">{okCount} ok</span>
                          {errorCount > 0 && <span className="sad-summary-error">{errorCount} error</span>}
                          {staleCount > 0 && <span className="sad-summary-stale">{staleCount} stale</span>}
                          {unknownCount > 0 && <span className="sad-summary-unknown">{unknownCount} unchecked</span>}
                        </span>
                      )}
                    </h3>
                    {market.error && <div className="sad-error-text">{market.error}</div>}
                    {sources.length === 0 && !market.error && (
                      <div className="sad-empty">No sources configured.</div>
                    )}
                    {sources.length > 0 && (
                      <div className="sad-scraper-list">
                        {sources.map(src => (
                          <div key={src.id} className={`sad-scraper-item${src.stale === true ? ' sad-scraper-stale-row' : ''}`}>
                            <span className={`sad-scraper-status sad-scraper-${(src.status || 'unknown').toLowerCase()}`}>
                              {src.status || '—'}
                            </span>
                            <span className="sad-scraper-name">{src.name || src.url || 'Unnamed'}</span>
                            {src.sourceType && (
                              <span className="sad-scraper-type">{src.sourceType}</span>
                            )}
                            {src.scope && (
                              <span className="sad-scraper-scope">{src.scope}</span>
                            )}
                            {src.stale === true && (
                              <span className="sad-scraper-stale-badge" title={`Cadence: every ${src.cadenceDays} days`}>
                                {src.daysSinceLastScrape != null ? `${src.daysSinceLastScrape}d ago` : 'never scraped'}
                              </span>
                            )}
                            {src.stale === false && (
                              <span className="sad-scraper-fresh-badge" title={`Cadence: every ${src.cadenceDays} days`}>
                                {src.daysSinceLastScrape}d ago
                              </span>
                            )}
                            {src.stale === null && src.lastScraped && (
                              <span className="sad-scraper-date">
                                {new Date(src.lastScraped).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* ── API Test Runner (collapsible) ── */}
        <section className="sad-section sad-collapsible">
          <button className="sad-section-toggle" onClick={() => setTestRunnerOpen(!testRunnerOpen)}>
            <div className="sad-toggle-title">
              <span className="sad-info-icon" data-tooltip="Run automated API tests across all system endpoints. Tests verify auth, routes, middleware, and data integrity across all markets.">i</span>
              <h2>API Test Runner</h2>
            </div>
            <span className={`sad-toggle-chevron ${testRunnerOpen ? 'sad-chevron-open' : ''}`}>&#9662;</span>
          </button>
          {testRunnerOpen && (
            <div className="sad-section-body">
              <div className="sad-test-runner">
                <div className="sad-test-controls">
                  <select
                    value={testSuite}
                    onChange={(e) => setTestSuite(e.target.value)}
                    disabled={isRunningTests}
                    className="sad-test-select"
                  >
                    <option value="all">All Tests</option>
                    <option value="unit">Unit Tests Only</option>
                    <option value="integration">Integration Tests Only</option>
                  </select>
                  <button
                    className="sad-test-run-btn"
                    onClick={handleRunTests}
                    disabled={isRunningTests}
                  >
                    {isRunningTests ? '⏳ Running...' : '🧪 Run API Tests'}
                  </button>
                </div>

                {/* Test Console Output */}
                {testConsoleOutput.length > 0 && (
                  <div className="sad-test-console-wrapper">
                    <div className="sad-test-console-header" onClick={() => setIsTestConsoleExpanded(!isTestConsoleExpanded)}>
                      <h4>Test Output</h4>
                      <div className="sad-test-console-actions">
                        <button className="sad-test-action-btn" onClick={(e) => { e.stopPropagation(); copyTestConsole(); }} title="Copy to clipboard">📋 Copy</button>
                        <button className="sad-test-action-btn" onClick={(e) => { e.stopPropagation(); downloadTestConsole(); }} title="Download as text file">⬇️ Download</button>
                        <button className="sad-test-action-btn sad-test-action-clear" onClick={(e) => { e.stopPropagation(); setTestConsoleOutput([]); }} title="Clear console">Clear</button>
                        <span className="sad-test-collapse-indicator">{isTestConsoleExpanded ? '▼' : '▶'}</span>
                      </div>
                    </div>
                    {isTestConsoleExpanded && (
                      <div className="sad-test-terminal">
                        <div className="sad-test-terminal-header">
                          <span className="sad-test-terminal-title">Test Results</span>
                          <div className="sad-test-terminal-dots">
                            <span className="sad-dot red"></span>
                            <span className="sad-dot yellow"></span>
                            <span className="sad-dot green"></span>
                          </div>
                        </div>
                        <div className="sad-test-terminal-body" ref={testConsoleRef}>
                          {testConsoleOutput.map((log, index) => (
                            <div key={index} className={`sad-test-line ${log.type}`}>
                              <span className="sad-test-timestamp">[{log.timestamp}]</span>
                              <span className="sad-test-message">{log.message}</span>
                            </div>
                          ))}
                          {isRunningTests && (
                            <div className="sad-test-line">
                              <span className="sad-test-cursor">▊</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

      </div>

      {/* ── Claim Approval Modal ── */}
      {claimModalOpen && claimModalData && (
        <div className="sad-modal-overlay" onClick={() => setClaimModalOpen(false)}>
          <div className="sad-modal" onClick={e => e.stopPropagation()}>
            <div className="sad-modal-header">
              <h3>Approve Venue Claim</h3>
              <button className="sad-modal-close" onClick={() => setClaimModalOpen(false)}>&times;</button>
            </div>
            <div className="sad-modal-body">
              {claimError && <div className="sad-modal-error">{claimError}</div>}

              <div className="sad-modal-field">
                <label>Name</label>
                <div className="sad-modal-field-value">{claimModalData.name || 'Not provided'}</div>
              </div>

              <div className="sad-modal-field">
                <label>Email</label>
                <div className="sad-modal-field-value">{claimModalData.email}</div>
              </div>

              {claimModalData.venue_name && (
                <div className="sad-modal-field">
                  <label>Claimed Venue</label>
                  <div className="sad-modal-field-value">
                    {claimModalData.venue_name}{claimModalData.venue_city ? ` - ${claimModalData.venue_city}` : ''}
                  </div>
                </div>
              )}

              <div className="sad-modal-field">
                <label>Link to Restaurant</label>
                {restaurantsLoading ? (
                  <div className="sad-loading" style={{ padding: '8px 0' }}>Loading restaurants...</div>
                ) : (
                  <select
                    className="sad-modal-select"
                    value={selectedRestaurant}
                    onChange={e => setSelectedRestaurant(e.target.value)}
                  >
                    <option value="">Select a restaurant...</option>
                    {restaurants.map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                )}
              </div>

              <div className="sad-modal-actions">
                <button className="sad-modal-cancel-btn" onClick={() => setClaimModalOpen(false)}>
                  Cancel
                </button>
                <button
                  className="sad-modal-approve-btn"
                  onClick={handleApproveClaim}
                  disabled={!selectedRestaurant || approving}
                >
                  {approving ? 'Creating Account...' : 'Approve & Create Account'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Success Modal ── */}
      {successModalOpen && successModalData && (
        <div className="sad-modal-overlay" onClick={() => setSuccessModalOpen(false)}>
          <div className="sad-modal" onClick={e => e.stopPropagation()}>
            <div className="sad-modal-body" style={{ paddingTop: 28 }}>
              <div className="sad-success-check">{'\u2713'}</div>
              <h3 className="sad-success-title">Account Created</h3>
              <p className="sad-success-subtitle">
                Venue owner account has been provisioned and linked.
              </p>

              <div className="sad-success-details">
                <div className="sad-success-detail-row">
                  <span className="sad-success-detail-label">Email</span>
                  <span className="sad-success-detail-value">{successModalData.email}</span>
                </div>
                <div className="sad-success-detail-row">
                  <span className="sad-success-detail-label">Venue</span>
                  <span className="sad-success-detail-value">{successModalData.venueName}</span>
                </div>
                <div className="sad-success-detail-row">
                  <span className="sad-success-detail-label">Temp Password</span>
                  <span className="sad-success-detail-value">Welcome2026!</span>
                </div>
              </div>

              <button
                className={`sad-copy-btn ${copiedEmail ? 'sad-copy-btn--copied' : ''}`}
                onClick={handleCopyEmail}
              >
                {copiedEmail ? '\u2713 Email Copied to Clipboard' : 'Copy Welcome Email'}
              </button>

              <button
                className="sad-modal-done-btn"
                onClick={() => setSuccessModalOpen(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Upgrade Success Modal ── */}
      {upgradeSuccessModal && (
        <div className="sad-modal-overlay" onClick={() => setUpgradeSuccessModal(null)}>
          <div className="sad-modal" onClick={e => e.stopPropagation()}>
            <div className="sad-modal-body" style={{ paddingTop: 28 }}>
              <div className="sad-success-check" style={{ background: '#f3e8ff', color: '#9333ea' }}>{'\u2B06'}</div>
              <h3 className="sad-success-title">Upgrade Approved</h3>
              <p className="sad-success-subtitle">
                User account and venue tier have been upgraded.
              </p>

              <div className="sad-success-details">
                <div className="sad-success-detail-row">
                  <span className="sad-success-detail-label">User</span>
                  <span className="sad-success-detail-value">{upgradeSuccessModal.name}</span>
                </div>
                <div className="sad-success-detail-row">
                  <span className="sad-success-detail-label">Venue</span>
                  <span className="sad-success-detail-value">{upgradeSuccessModal.venue_name}</span>
                </div>
                <div className="sad-success-detail-row">
                  <span className="sad-success-detail-label">New Tier</span>
                  <span className="sad-success-detail-value" style={{ color: '#9333ea', fontWeight: 600 }}>
                    {(upgradeSuccessModal.requested_tier || 'silver').charAt(0).toUpperCase() + (upgradeSuccessModal.requested_tier || 'silver').slice(1)}
                  </span>
                </div>
              </div>

              <button
                className="sad-modal-done-btn"
                onClick={() => setUpgradeSuccessModal(null)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default SuperAdminDashboard;
